import { Injectable, inject } from '@angular/core';

import { UserUseCases, USER_USE_CASES } from '@app/domain/use-cases/user/user.use-cases.contract';
import { AppError, ErrorTypes } from '@app/entities/models/error/error.model';
import { User } from '@app/entities/models/user/user.model';
import { PtrabSessionManager } from '@app/services/ptrab-session.manager.service';
import { AppManagerService } from '@services/app/app.manager.service';
import { AuthService } from '@services/auth/auth.service';
import { ErrorService } from '@services/error/error.service';
import { Logger } from '@services/logger/logger.service';
import { NavigationEvent, NavigationEvents, NavigationService } from '@services/navigation/navigation.service';

import { PAGES } from '@/enums/pages.enum';

@Injectable({
  providedIn: 'root'
})
export class AuthGuardService {
  private readonly logger = new Logger('AuthGuardService');

  #userUseCases: UserUseCases = inject(USER_USE_CASES);

  constructor(
    private appManagerService: AppManagerService,
    private authService: AuthService,
    private errorService: ErrorService,
    private navigationService: NavigationService,
    private ptrabSessionManager: PtrabSessionManager
  ) {}

  async canLoad(): Promise<boolean> {
    return this.authService.isLoggedIn() || this.authService.hasActivo2Session();
  }

  async canActivate(): Promise<boolean> {
    try {
      if (this.authService.isPendingMsalLogin) {
        this.logger.debug('canActivate: pending MSAL login, silently blocking route');
        return false;
      }

      const hasAdfsSession = await this.authService.hasSession();

      if (!hasAdfsSession) {
        const message = 'User is not logged, unable to open a private page';
        this.logger.error(message);
        this.errorService.add(
          new AppError(ErrorTypes.FORBIDDEN, message, {
            hash: location.hash
          })
        );
        return false;
      }

      this.logger.debug('Waiting appManager is ready');

      this.appManagerService.init(this.ptrabSessionManager);
      await this.appManagerService.ready();
      this.logger.debug('AppManager is ready!');

      const user: User = await this.#userUseCases.getStoredUser();
      if (!user) {
        this.logger.error('User info not in storage');
        this.errorService.add(new AppError(ErrorTypes.GETTING_USER_INFO));
        return false;
      }

      const accepted = user.acceptLegal;
      if (!accepted) {
        this.logger.debug('Cannot enter on page as user should accept terms first');
        const navEvent = new NavigationEvent(NavigationEvents.Push, { path: PAGES.TERMS_AND_CONDITIONS_INTER });
        this.navigationService.navigate(navEvent);
        return false;
      }

      this.logger.debug('Allowed to enter on page');
      return true;
    } catch (error) {
      this.logger.error('Error on canActivate:', error);
      return false;
    }
  }
}
